import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2, PanelRightOpen } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { APIProvider } from "@vis.gl/react-google-maps";
import { FormProvider, useForm, useWatch, UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetCustomV1Bo } from "@/api/customs.api";
import { toast } from "react-toastify";
import { useUpdateFinalizedCustomQuotation } from "@/api/expedition.api";
import { useGetAllProducts } from "@/api/product.api";
import { useGetAllProductsUsaByCompany } from "@/api/productUsa.api";
import {
  CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE,
  UpdateFinalizedCustomQuotationDefaultValues,
  updateFinalizedCustomQuotationPayload,
} from "@/components/backOffice/expeditionForm/expeditionFormHelper";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  CustomQuotationUpdateBoFormData,
  UpdateFinalizedCustomQuotationFormData,
  UpdateFinalizedCustomQuotationFormSchema,
} from "@/types/forms/bo/expeditionBo.form";
import { ExpeditionFormProps } from "@/components/backOffice/expeditionForm/expeditionForm.types";
import { ExpeditionHeader } from "@/components/backOffice/expeditionForm/expeditionHeader";
import { DeliveryContent } from "@/components/backOffice/expeditionForm/shared/sections/deliveryContent/deliveryContent";
import { ExpeditionDeliveryInfo } from "@/components/backOffice/expeditionForm/shared/sections/deliveryInfo/deliveryInfo";
import {
  SideContent,
  SideContentMobileModal,
} from "@/components/backOffice/expeditionForm/shared/sections/sidePanel/sideContent";
import { PackagingInstructionsSection } from "@/components/backOffice/expeditionForm/shared/sections/finalizeDetails/packagingInstructionsSection";
import { TrackingInfo } from "@/components/backOffice/expeditionForm/shared/finalize/trackingInfo";
import { FinalizeApiEnum } from "@/types/finalizeApi.type";

export const CustomQuotationUpdateFinalizedFormStep = ({
  expedition,
  company,
  apiEnum,
}: ExpeditionFormProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const navigate = useNavigate();
  const methods = useForm<UpdateFinalizedCustomQuotationFormData>({
    resolver: zodResolver(UpdateFinalizedCustomQuotationFormSchema),
    defaultValues: UpdateFinalizedCustomQuotationDefaultValues({ expedition, company, apiEnum }),
  });
  const sectionMethods = methods as unknown as UseFormReturn<CustomQuotationUpdateBoFormData>;

  const { mutateAsync: updateFinalizedCustomQuotation, isPending } =
    useUpdateFinalizedCustomQuotation();
  const [isSideContentOpen, setIsSideContentOpen] = useState(false);

  const importerId = useWatch({ control: methods.control, name: "importerId" });
  const countryCode = useWatch({ control: methods.control, name: "deliveryInfo.countryCode" });
  const apiEnumWatch = useWatch({ control: methods.control, name: "apiEnum" });

  const {
    data: products,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useGetAllProducts();
  const {
    data: productsUSA,
    isLoading: isLoadingProductsUSA,
    isError: isErrorProductsUSA,
  } = useGetAllProductsUsaByCompany(company.id, importerId);
  const {
    data: custom,
    isLoading: isLoadingCustoms,
    isError: isErrorCustoms,
  } = useGetCustomV1Bo(expedition.customQuotation?.customId ?? "");

  if (isLoadingProducts || isLoadingProductsUSA || isLoadingCustoms) {
    return <Loader2 className="animate-spin" />;
  }

  if (isErrorProducts || isErrorProductsUSA || isErrorCustoms) {
    return <div>{t("formUpdateFinalized.loadError")}</div>;
  }

  const isUsaProducts = countryCode === "US";
  const productsList = isUsaProducts ? productsUSA : products?.data;
  const isSimplified = apiEnumWatch === FinalizeApiEnum.NONE_SIMPLIFIED;
  const isManualTracking =
    apiEnumWatch === FinalizeApiEnum.NONE || apiEnumWatch === FinalizeApiEnum.NONE_SIMPLIFIED;

  const onSubmit = async (data: UpdateFinalizedCustomQuotationFormData) => {
    if (expedition.notification?.some((n) => n.readBy == null)) {
      toast.error(t("formUpdateFinalized.unreadNotifications"), { position: "top-center" });
      return;
    }
    await updateFinalizedCustomQuotation({
      id: expedition.id,
      payload: updateFinalizedCustomQuotationPayload({ data, expedition }),
    });
    navigate(-1);
  };

  return (
    <APIProvider apiKey="AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw">
      <FormProvider {...sectionMethods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          className="@container absolute inset-0 flex flex-col overflow-hidden"
        >
          <ExpeditionHeader expedition={expedition} mode="updateFinalized" />
          <div className="relative flex w-full flex-col justify-between gap-2 overflow-hidden @min-[896px]:flex-row">
            <Accordion
              type="multiple"
              className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
              defaultValue={[
                "deliveryInfo",
                "deliveryContent",
                "packagingInstructions",
                "trackingInfo",
              ]}
            >
              <ExpeditionDeliveryInfo expedition={expedition} custom={custom} />
              {!isSimplified && (
                <DeliveryContent
                  products={productsList}
                  isUsaProducts={isUsaProducts}
                  companyId={company.id}
                  countryCode={countryCode}
                  viaImportedInvoice={CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE}
                />
              )}
              <PackagingInstructionsSection />
              {isManualTracking && <TrackingInfo />}
              <Button type="submit" className="w-fit self-start" disabled={isPending}>
                {isPending ? <Loader2 className="animate-spin" /> : t("formUpdateFinalized.submit")}
              </Button>
            </Accordion>
            <div className="hidden min-h-0 w-[440px] flex-col gap-4 overflow-y-auto p-4 @min-[896px]:flex">
              <SideContent expedition={expedition} company={company} custom={custom} />
            </div>
            <Button
              type="button"
              variant="default"
              onClick={() => setIsSideContentOpen(true)}
              aria-label={t("formUpdateFinalized.openDetails")}
              className="absolute right-4 bottom-4 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg @min-[896px]:hidden [&_svg]:h-6 [&_svg]:w-6"
            >
              <PanelRightOpen />
            </Button>
            <SideContentMobileModal
              open={isSideContentOpen}
              onClose={() => setIsSideContentOpen(false)}
              expedition={expedition}
              company={company}
              custom={custom}
            />
          </div>
        </form>
      </FormProvider>
    </APIProvider>
  );
};
